import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { ApContentModule, ApContentService } from '@ap/content';
import { Betaalverzoek, Verklaring } from '@ap/interface-apic-betaalverzoek';
import { ApFormService } from '@ap/form';
import { ApCookieService } from '@ap/persistence';
import { ApAnimationComponent, AvailableAnimations } from '@ap/animation';
import { ApButtonComponent, ApSubmitTextComponent } from '@ap/button';
import { SessiebeheerModule } from '@ap/interface-apic-sessiebeheer';

import { BetaalverzoekErrorType } from '../../betaalverzoek.model';
import { BetaalverzoekNavigation } from '../../betaalverzoek.navigation';
import { BetaalverzoekService } from '../../betaalverzoek.service';
import { OpenAmountService } from '../../domain/open-amount/open-amount.service';
import { PaymentRequestDetailsComponent } from '../../domain/payment-request-details/payment-request-details.component';
import { OpenAmountComponent } from '../../domain/open-amount/open-amount.component';
import { BetaalverzoekInfoComponent } from '../../domain/betaalverzoek-info/betaalverzoek-info.component';

import content from './landing-page.content.json';

/**
 * @ngdoc component
 * @name LandingPageComponent
 *
 * @description
 * Component that provides functionality for the payment request landing page
 */
@Component({
    selector: 'landing-page',
    templateUrl: './landing-page.component.html',
    imports: [
        ApButtonComponent,
        ApContentModule,
        PaymentRequestDetailsComponent,
        OpenAmountComponent,
        ReactiveFormsModule,
        RouterModule,
        SessiebeheerModule,
        BetaalverzoekInfoComponent,
        ApAnimationComponent,
        ApSubmitTextComponent
    ],
    providers: [ApFormService, ApCookieService]
})
export class LandingPageComponent implements OnInit {
    public betaalverzoekFormControl: FormControl<number>;
    public betaalverzoek: Betaalverzoek;
    public betaalverzoekId: string;
    public submitButtonText: string;
    public submitButtonLoadingText: string;
    public isSubmitting = false;
    public alreadyPayed: boolean = false;
    public paymentRequestCompletedAnimation = AvailableAnimations.PAYMENTREQUESTCOMPLETED;

    public constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly betaalverzoekNavigation: BetaalverzoekNavigation,
        private readonly betaalverzoekService: BetaalverzoekService,
        private readonly contentService: ApContentService,
        private readonly formService: ApFormService,
        private readonly cookieService: ApCookieService
    ) {}

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.submitButtonText = this.contentService.get('landing-page.submit.button');
        this.submitButtonLoadingText = this.contentService.get('landing-page.submit.button.loading');
        this.betaalverzoekId = this.activatedRoute.snapshot.params.id;
        this.getPaymentRequest(this.betaalverzoekId, this.activatedRoute.snapshot.params.ac);
        this.getAlreadyPayedCookie(this.betaalverzoekId);
        this.betaalverzoekService.setShowHeaderIcon(false);
    }

    public getAlreadyPayedCookie(betaalverzoekId: string) {
        if (betaalverzoekId === this.cookieService.get(betaalverzoekId)) {
            this.alreadyPayed = true;
        }
    }

    public payPaymentRequestAgain(): void {
        this.alreadyPayed = false;
    }

    public submitIdealPaymentTransaction(): void {
        if (this.formService.verify(this.betaalverzoekFormControl)) {
            this.isSubmitting = true;
            const openAmount = this.betaalverzoekFormControl.value;
            this.betaalverzoekService.createBetaaltransactie(this.betaalverzoekId, null, null, openAmount).subscribe({
                next: (locationHeader) => this.betaalverzoekNavigation.external(locationHeader),
                error: (error) => {
                    let errorType: BetaalverzoekErrorType = BetaalverzoekErrorType.TRANSACTION_REQUEST_SERVICE_UNAVAILABLE;
                    if (
                        error instanceof HttpErrorResponse &&
                        error.status === 400 &&
                        error.error.code === Verklaring.BV_MONTHLY_AMOUNT_EXCEEDED
                    ) {
                        errorType = BetaalverzoekErrorType.TRANSACTION_INCOMING_MONTHLY_AMOUNT_EXCEEDED;
                    }
                    this.betaalverzoekNavigation.toErrorPage(BetaalverzoekService.generateErrorData(errorType, false));
                },
                complete: () => (this.isSubmitting = false)
            });
        }
    }

    private getPaymentRequest(betaalverzoekId: string, autorisatieCode: string): void {
        this.betaalverzoekService.getBetaalverzoek(betaalverzoekId, autorisatieCode).subscribe({
            next: (betaalverzoek: Betaalverzoek) => {
                this.betaalverzoek = betaalverzoek;
                this.betaalverzoekFormControl = this.createForm(!betaalverzoek.bedrag, betaalverzoek.maximaal_bedrag?.bedrag);
            },
            error: (error) => {
                let errorType: BetaalverzoekErrorType = BetaalverzoekErrorType.TRANSACTION_REQUEST_SERVICE_UNAVAILABLE;
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 400 &&
                    error.error?.code === Verklaring.BV_MONTHLY_AMOUNT_EXCEEDED
                ) {
                    errorType = BetaalverzoekErrorType.TRANSACTION_INCOMING_MONTHLY_AMOUNT_EXCEEDED;
                } else if (error instanceof HttpErrorResponse && error.status === 404) {
                    errorType = BetaalverzoekErrorType.PAYMENT_REQUEST_GENERIC_ERROR;
                }
                this.betaalverzoekNavigation.toErrorPage(BetaalverzoekService.generateErrorData(errorType, false));
            }
        });
    }

    private createForm(openAmount: boolean, maxAmount: number): FormControl<number> {
        return openAmount ? OpenAmountService.getOpenAmountFormControl(maxAmount) : new FormControl(undefined);
    }
}
